/**
 * Origins of the two sign-in destinations reachable from the marketing site.
 *
 * Both are separate apps on their own subdomains, so every link to them is a
 * full page navigation — never a react-router route.
 *
 * The officer/clerk portal is `gov.munifocus.com` (Government Portal) and the
 * owner-facing client portal is `public.munifocus.com` (Public Portal).
 *
 * Dev points these at the local Vite servers (gov on 4601, public on
 * 4603); the production domains are the fallback.
 */
/**
 * Resolve a configured portal origin, falling back whenever the value can't
 * produce a cross-origin link.
 *
 * `??` alone is not enough. An env var that is *defined but blank* — `VITE_GOV_URL=`
 * in a .env file, or an unset `${{ }}` expression in a workflow — arrives as `''`,
 * which is not nullish. That would make `${origin}/login` the same-origin path
 * `/login`, silently turning both portal links into in-app navigations to a route
 * that doesn't exist. A relative or non-http value fails the same way, so both are
 * rejected here rather than shipped as a broken link.
 */
export function portalOrigin(configured: string | undefined, fallback: string): string {
  const value = configured?.trim();
  if (!value || !/^https?:\/\//i.test(value)) return fallback;
  return value.replace(/\/$/, '');
}

/** Officer / clerk portal (`apps/gov`). */
const GOV_URL = portalOrigin(import.meta.env.VITE_GOV_URL, 'https://gov.munifocus.com');

/** Property owner / occupant portal (`apps/public`). */
const PUBLIC_URL = portalOrigin(import.meta.env.VITE_PUBLIC_URL, 'https://public.munifocus.com');

/** Sign-in on the Government Portal. */
export const govLoginUrl = `${GOV_URL}/login`;

/** Sign-in on the Public Portal. */
export const publicLoginUrl = `${PUBLIC_URL}/login`;

/**
 * The Public Portal's report form — the one part of it that needs no account.
 * Someone looking for "how do I report a property" lands on this site first.
 */
export const publicReportUrl = `${PUBLIC_URL}/report`;

export { GOV_URL, PUBLIC_URL };
